import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, ArrowRight, Github, Linkedin, Mail, MessageCircle } from 'lucide-react';

const socials = [
  { label: 'GitHub', icon: Github, href: 'https://github.com/Anasaljammal' },
  { label: 'LinkedIn', icon: Linkedin, href: 'https://www.linkedin.com/in/anas-aljammal-47a512226' },
  { label: 'WhatsApp', icon: MessageCircle, href: 'https://wa.me/963997270886' },
  { label: 'Email', icon: Mail, href: 'mailto:anasaljammal686@gmail.com' },
];

const Hero: React.FC = () => {
  const { t } = useTranslation();

  const stats = [
    { value: '4+', label: t('hero.stats.years') },
    { value: '9+', label: t('hero.stats.projects') },
    { value: 'iOS · Android', label: t('hero.stats.platforms') },
  ];

  return (
    /* Was min-h-[100svh] with the content vertically centred, which on a laptop
       left a screenful of paper above the greeting and pushed the stats row —
       the only numbers on the page — below the fold. Sized to its content with
       a floor instead, so the fold lands inside the stats. */
    <section id="home" className="flex min-h-[82svh] items-center pb-12 pt-24 sm:pb-16 sm:pt-28">
      <div className="container-page w-full">
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-3">
          <p className="ui text-[15px] text-graphite">{t('hero.greeting')}</p>

          <p className="ui inline-flex items-center gap-2.5 text-[13px] text-graphite">
            <span
              aria-hidden="true"
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-lacquer-bright"
            />
            {t('hero.available')}
          </p>
        </div>

        {/* The name carries the <h1>, and the size.
            It used to be a 13px letterspaced label sitting above the job title at
            display size — on a personal site that put the loudest voice on the
            role and left the person almost unreadable. Reversed: name at display
            size in ink, role beneath it at a third the size in the accent. */}
        <div className="mt-5 max-w-headline sm:mt-6">
          <h1 className="hero-name animate-hero">Anas Jammal</h1>
          <p className="hero-role animate-hero mt-3 sm:mt-4" style={{ animationDelay: '90ms' }}>
            {t('hero.title')}
          </p>
        </div>

        <p
          className="animate-hero mt-7 max-w-prose text-[17px] leading-[1.65] text-graphite sm:mt-8"
          style={{ animationDelay: '170ms' }}
        >
          {t('hero.description')}
        </p>

        <div
          className="animate-hero mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center"
          style={{ animationDelay: '250ms' }}
        >
          <a href="#projects" className="btn-primary group">
            {t('hero.viewWork')}
            <ArrowRight
              size={17}
              aria-hidden="true"
              className="transition-transform duration-200 group-hover:translate-x-1"
            />
          </a>
          <a
            href="/Anas_Khaled_Jammal.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
          >
            <Download size={17} aria-hidden="true" />
            {t('hero.downloadCV')}
          </a>

          <ul className="mt-2 flex items-center gap-1 sm:ml-4 sm:mt-0">
            {socials.map(({ label, icon: Icon, href }) => (
              <li key={label}>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex h-11 w-11 items-center justify-center text-graphite transition-all duration-200 hover:-translate-y-0.5 hover:text-lacquer-bright"
                >
                  <Icon size={18} aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        </div>

        <hr className="rule mt-12 sm:mt-14" />
        <dl className="animate-hero grid grid-cols-3 gap-x-4 gap-y-2 pt-6" style={{ animationDelay: '330ms' }}>
          {stats.map((s) => (
            <div key={s.label}>
              <dd className="ui text-[18px] font-bold leading-tight text-ink sm:text-[22px]">
                {s.value}
              </dd>
              <dt className="ui mt-1.5 text-[11px] uppercase leading-snug tracking-[0.14em] text-graphite sm:text-[12px]">
                {s.label}
              </dt>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
};

export default Hero;
